function isValidDateBirthMdy(value) {
  if (!value || typeof value !== 'string') return false
  const match = value.trim().match(/^(\d{2})-(\d{2})-(\d{4})$/)
  if (!match) return false
  const month = Number(match[1])
  const day = Number(match[2])
  const year = Number(match[3])
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900) return false
  const date = new Date(year, month - 1, day)
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
}

export const RELATIONSHIP_OPTIONS = [
  { value: 'spouse', text: 'Cónyuge' },
  { value: 'child', text: 'Hijo o hija' },
  { value: 'parent', text: 'Padre o madre' },
  { value: 'other', text: 'Otro' },
]

export function emptyFamilyMember() {
  return {
    name: '',
    lastname: '',
    date_birth: '',
    relationship: 'spouse',
    phone: '',
    email: '',
  }
}

/**
 * Edad cumplida a partir de MM-DD-YYYY.
 * @param {string} value
 * @param {Date} [today]
 * @returns {number|null}
 */
export function ageFromDateBirth(value, today = new Date()) {
  if (!isValidDateBirthMdy(value)) return null
  const [month, day, year] = value.trim().split('-').map(Number)
  let age = today.getFullYear() - year
  const currentMonth = today.getMonth() + 1
  const currentDay = today.getDate()
  if (currentMonth < month || (currentMonth === month && currentDay < day)) age -= 1
  return age
}

export const DEFAULT_AGE_LIMITS = { childMinAge: 2, adultMinAge: 18 }

/**
 * Edades del plan (child_min_age y adult_min_age). Sin plan, 2 y 18 como el contrato.
 * @param {Object|null} plan
 * @returns {{childMinAge: number, adultMinAge: number}}
 */
export function familyAgeLimits(plan) {
  const source = (plan && plan.memberships_parameters) || plan || {}
  const child = Number(source.child_min_age)
  const adult = Number(source.adult_min_age)
  return {
    childMinAge: source.child_min_age != null && Number.isFinite(child) ? child : DEFAULT_AGE_LIMITS.childMinAge,
    adultMinAge: source.adult_min_age != null && Number.isFinite(adult) ? adult : DEFAULT_AGE_LIMITS.adultMinAge,
  }
}

/**
 * @param {number|null} age
 * @param {{childMinAge: number, adultMinAge: number}} [limits]
 * @returns {'underage'|'child'|'adult'|null}
 */
export function memberAudience(age, limits = DEFAULT_AGE_LIMITS) {
  if (age == null) return null
  if (age < limits.childMinAge) return 'underage'
  if (age < limits.adultMinAge) return 'child'
  return 'adult'
}

/**
 * @param {Object} member
 * @param {string} label
 * @returns {string|null}
 */
export function validateFamilyMember(member, label, limits = DEFAULT_AGE_LIMITS) {
  if (!member || !member.name || !member.lastname) {
    return `${label}: completa nombre y apellido.`
  }
  if (!member.date_birth || !isValidDateBirthMdy(member.date_birth)) {
    return `${label}: la fecha de nacimiento debe tener el formato MM-DD-YYYY.`
  }
  if (!member.relationship) {
    return `${label}: indica el parentesco.`
  }
  const audience = memberAudience(ageFromDateBirth(member.date_birth), limits)
  if (audience === 'underage') {
    return `${label}: los menores de ${limits.childMinAge} años no pueden entrar al plan.`
  }
  return null
}

/**
 * Niños de 2 a 17 no llevan teléfono ni correo. El backend los ignora si llegan.
 * @param {Object} member
 * @returns {Object}
 */
export function familyMemberPayload(member, limits = DEFAULT_AGE_LIMITS) {
  const audience = memberAudience(ageFromDateBirth(member.date_birth), limits)
  const payload = {
    name: String(member.name || '').trim(),
    lastname: String(member.lastname || '').trim(),
    date_birth: String(member.date_birth || '').trim(),
    relationship: member.relationship,
  }
  if (audience === 'adult') {
    if (member.phone && String(member.phone).trim()) payload.phone = String(member.phone).trim()
    if (member.email && String(member.email).trim()) payload.email = String(member.email).trim()
  }
  return payload
}

export const GUARANTEE_REASON_COPY = {
  window_expired: 'Pasaron los 30 días.',
  benefits_used: 'Ya usó exámenes o estudios.',
  open_request: 'Ya hay una solicitud en revisión.',
  already_refunded: 'Ya fue reembolsada.',
  canceled: 'El plan no aplica.',
  not_paid: 'El plan no aplica.',
  not_online: 'El plan no aplica.',
}

export const REGULAR_VISIT_TYPES = [
  { value: 1, text: 'Follow Up' },
  { value: 2, text: 'New Consultation' },
  { value: 3, text: 'New patient' },
  { value: 4, text: 'Pending Injection' },
  { value: 5, text: 'Pending Procedure' },
  { value: 6, text: 'Result' },
  { value: 7, text: 'Physical school' },
  { value: 8, text: 'Membership' },
  { value: 13, text: 'IV therapies - NP' },
  { value: 14, text: 'IV therapies - NC' },
]

/**
 * Lista dentro de { status, data }, data.data o el arreglo directo.
 * @param {*} payload
 * @returns {Array}
 */
export function unwrapList(payload) {
  if (!payload) return []
  if (Array.isArray(payload)) return payload
  const body = payload.data !== undefined && payload.status ? payload.data : payload
  if (Array.isArray(body)) return body
  if (!body || typeof body !== 'object') return []
  if (Array.isArray(body.data)) return body.data
  if (Array.isArray(body.members)) return body.members
  if (Array.isArray(body.companies)) return body.companies
  if (Array.isArray(body.employees)) return body.employees
  if (Array.isArray(body.agents)) return body.agents
  return []
}

/**
 * El titular del grupo llega con member_type 'holder'.
 * @param {Object} member
 * @returns {boolean}
 */
export function isFamilyHolder(member) {
  return !!member && member.member_type === 'holder'
}

/**
 * Cada empresa de la lista llega con la forma del detalle: { company: { id, name }, subscription, seats, employees }.
 * @param {Object} item
 * @returns {{id: number|null, name: string}}
 */
export function companyOf(item) {
  const company = (item && item.company) || item || {}
  return { id: company.id != null ? company.id : null, name: company.name || '' }
}

export const PAY_STATUS_COPY = {
  0: 'Sin pagar',
  1: 'Activo',
  3: 'Pago fallido',
  4: 'Cancelado',
}

/**
 * Objeto de detalle dentro de { status, data }.
 * @param {*} payload
 * @returns {Object|null}
 */
export function unwrapRecord(payload) {
  if (!payload || typeof payload !== 'object') return null
  if (payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)) return payload.data
  return payload
}
